import { getRoute } from 'helpers/route.helper/route.helper'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import filterStore from 'store/filter/filter'
import Card from 'ui/atoms/Card/Card'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Title from 'ui/molecules/Title/Title'
import { useSnapshot } from 'valtio'
import { FollowupAppliedSettingValues } from './FollowupAppliedSettingValues'
import type { FollowupProductionHeaderTab, FollowupProductionTab } from './FollowupProduction.types'
import { getNamedOptJobType } from './followupAppliedSettings.helper'

type FollowupAppliedSettingsProps = {
  tab: FollowupProductionTab
  headerTab: FollowupProductionHeaderTab
  datasetStartTime?: ISODateTime
  datasetEndTime?: ISODateTime
}

/** The `applied_settings_values` cell of a tab; it shows only on a page with a header filter, which narrows its columns. */
export function FollowupAppliedSettings({
  tab,
  headerTab,
  datasetStartTime,
  datasetEndTime,
}: FollowupAppliedSettingsProps): ReactNode {
  const { t } = useTranslation()
  const { hasAccess } = useAuth()
  const { filterComponentPerRoute } = useSnapshot(filterStore)
  const hasHeaderFilter = !!filterComponentPerRoute[getRoute()]
  const cell = tab.applied_settings_values
  const hasAccessToSettingsTable = hasAccess({
    module: 'followupproduction',
    submodule: 'followupproduction_setting_table',
  })

  const overrideAlias = cell?.override_alias || headerTab.override_alias

  if (!hasHeaderFilter || !hasAccessToSettingsTable || !cell || !getNamedOptJobType(overrideAlias)) {
    return null
  }

  return (
    <div>
      <Title title={cell.title ?? ''} />
      <div className="relative z-0 w-full min-w-0">
        <Card
          title={t(`Applied settings values`)}
          titleSize="large"
          collapsible
          defaultCollapsed
          rememberCollapsed
          style="table"
        >
          <FollowupAppliedSettingValues
            overrideAlias={overrideAlias}
            period={
              datasetStartTime && datasetEndTime ? { startTime: datasetStartTime, endTime: datasetEndTime } : null
            }
          />
        </Card>
      </div>
    </div>
  )
}
